export type InterviewTranscriptionOutcomeKind =
  | 'success'
  | 'no_speech'
  | 'provider_error'
  | 'processing_error'
  | 'invalid_audio'
  | 'rate_limit'
  | 'auth_error'
  | 'terminal_error';

export type InterviewTranscriptionRecoveryClass =
  | 'success'
  | 'infrastructure'
  | 'rate_limit'
  | 'audio'
  | 'terminal';

export type InterviewTranscriptionOutcome = {
  kind: InterviewTranscriptionOutcomeKind;
  consumeNoAnswerAttempt: boolean;
  retryable: boolean;
  remainOnSameQuestion: boolean;
  message: string;
  recoveryClass: InterviewTranscriptionRecoveryClass;
  retryAfterMs?: number | null;
};

const INFRASTRUCTURE_MESSAGE = 'Koneksi sedang bermasalah. Jawabanmu tetap tersimpan dan TEMI akan mencoba lagi otomatis.';
const PROCESSING_MESSAGE = 'TEMI belum dapat membaca jawabanmu. Rekaman tetap tersimpan dan akan dicoba kembali otomatis.';
const INVALID_AUDIO_MESSAGE = 'Rekaman audio belum terbaca dengan jelas. TEMI akan meminta jawaban kembali secara otomatis.';
const RATE_LIMIT_MESSAGE = 'Layanan transkripsi sedang sibuk. Jawabanmu tetap tersimpan dan akan dicoba kembali otomatis.';
const AUTH_MESSAGE = 'Sesi interview tidak dapat memproses jawaban ini. Silakan masuk kembali melalui alur interview.';
const TERMINAL_MESSAGE = 'Jawaban belum dapat diproses karena sesi interview tidak valid.';

const resolveRetryAfterMs = (error: any) => {
  const retryAfterSeconds = Number(
    error?.response?.data?.retry_after_seconds
    ?? error?.response?.headers?.['retry-after']
    ?? error?.response?.headers?.get?.('retry-after')
  );
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.round(retryAfterSeconds * 1000)
    : null;
};

const buildOutcome = (
  kind: InterviewTranscriptionOutcomeKind,
  options: Partial<Omit<InterviewTranscriptionOutcome, 'kind'>> = {}
): InterviewTranscriptionOutcome => ({
  kind,
  consumeNoAnswerAttempt: kind === 'no_speech',
  retryable: kind === 'provider_error' || kind === 'processing_error' || kind === 'no_speech',
  remainOnSameQuestion: kind !== 'success',
  message: '',
  recoveryClass: kind === 'success'
    ? 'success'
    : kind === 'provider_error' || kind === 'processing_error'
    ? 'infrastructure'
    : kind === 'no_speech' || kind === 'invalid_audio'
    ? 'audio'
    : kind === 'rate_limit'
    ? 'rate_limit'
    : 'terminal',
  ...options,
});

const getTranscriptText = (payload: any) => String(
  payload?.raw_transcript || payload?.transcript || payload?.cleaned_transcript || ''
).trim();

export const resolveTranscriptionPayloadOutcome = (payload: any): InterviewTranscriptionOutcome => {
  const transcribeStatus = String(payload?.transcribe_status || '').trim().toLowerCase();
  const errorCode = String(payload?.error_code || '').trim().toUpperCase();
  const providerError = String(payload?.provider_error || '').trim();
  const failureReason = String(payload?.failure_reason || '').trim().toLowerCase();
  const serverMessage = typeof payload?.message === 'string' ? payload.message.trim() : '';

  if (errorCode === 'RATE_LIMITED' || failureReason === 'provider_rate_limited') {
    return buildOutcome('rate_limit', {
      retryable: true,
      retryAfterMs: Number.isFinite(Number(payload?.retry_after_seconds))
        ? Math.max(0, Number(payload.retry_after_seconds) * 1000)
        : null,
      message: serverMessage || RATE_LIMIT_MESSAGE,
    });
  }

  if (errorCode === 'STT_PROVIDER_ERROR' || transcribeStatus === 'provider_error' || providerError) {
    return buildOutcome('provider_error', {
      message: serverMessage || INFRASTRUCTURE_MESSAGE,
    });
  }

  if (errorCode === 'AUDIO_PROCESSING_ERROR' || transcribeStatus === 'processing_error') {
    return buildOutcome('processing_error', {
      message: serverMessage || PROCESSING_MESSAGE,
    });
  }

  if (errorCode === 'INVALID_AUDIO' || transcribeStatus === 'invalid_audio') {
    return buildOutcome('invalid_audio', {
      retryable: false,
      message: serverMessage || INVALID_AUDIO_MESSAGE,
    });
  }

  if (errorCode === 'CANDIDATE_SILENCE' || transcribeStatus === 'no_speech') {
    return buildOutcome('no_speech');
  }

  if (getTranscriptText(payload)) {
    return buildOutcome('success');
  }

  return buildOutcome('provider_error', {
    message: serverMessage || INFRASTRUCTURE_MESSAGE,
  });
};

export const resolveTranscriptionErrorOutcome = (error: any): InterviewTranscriptionOutcome => {
  const status = Number(error?.response?.status) || null;
  if (status === 401 || status === 403) {
    return buildOutcome('auth_error', {
      retryable: false,
      message: AUTH_MESSAGE,
    });
  }

  if (status === 429) {
    return buildOutcome('rate_limit', {
      retryable: true,
      retryAfterMs: resolveRetryAfterMs(error),
      message: RATE_LIMIT_MESSAGE,
    });
  }

  const payload = error?.response?.data;
  if (payload && typeof payload === 'object') {
    return resolveTranscriptionPayloadOutcome(payload);
  }

  if (status !== null && status >= 400 && status < 500 && ![408, 425].includes(status)) {
    return buildOutcome('terminal_error', {
      retryable: false,
      message: TERMINAL_MESSAGE,
    });
  }

  return buildOutcome('provider_error', {
    message: INFRASTRUCTURE_MESSAGE,
  });
};

export const shouldConsumeNoAnswerAttempt = (outcome: InterviewTranscriptionOutcome) => (
  outcome.kind === 'no_speech' && outcome.consumeNoAnswerAttempt
);
